import { firestoreService } from '../firebase/firestore';

const MANAGE_SUBSCRIPTION_ENDPOINT =
  process.env.REACT_APP_MANAGE_SUBSCRIPTION_ENDPOINT ||
  'https://us-central1-hoanguyendalat-13911.cloudfunctions.net/manageSubscription';

async function callManageSubscription(body) {
  const response = await fetch(MANAGE_SUBSCRIPTION_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || 'Subscription operation failed');
  }
  return data;
}

export const subscriberService = {
  /**
   * Create a new subscriber (public - uses Firestore create rule directly)
   */
  async createSubscriber(subscriberData) {
    try {
      const docId = await firestoreService.create('subscribers', subscriberData);
      return { success: true, id: docId };
    } catch (error) {
      console.error('Error creating subscriber:', error);
      throw error;
    }
  },

  /**
   * Check if email already exists (via Cloud Function - no public Firestore read)
   */
  async checkEmailExists(email) {
    try {
      const result = await callManageSubscription({
        action: 'check',
        email: email.toLowerCase().trim(),
      });
      if (!result.exists) {
        return [];
      }
      return [{ id: result.id, status: result.status, phone: result.phone }];
    } catch (error) {
      console.error('Error checking email:', error);
      throw error;
    }
  },

  /**
   * Update phone number on an existing subscriber (via Cloud Function)
   */
  async updatePhone(subscriberId, phone) {
    try {
      await callManageSubscription({
        action: 'updatePhone',
        subscriberId,
        phone,
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating subscriber phone:', error);
      throw error;
    }
  },

  /**
   * Reactivate an unsubscribed email (via Cloud Function)
   */
  async reactivateSubscription(subscriberId) {
    try {
      await callManageSubscription({
        action: 'reactivate',
        subscriberId,
      });
      return { success: true };
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      throw error;
    }
  },

  /**
   * Get all subscribers (admin only)
   */
  async getAllSubscribers() {
    try {
      const subscribers = await firestoreService.getAll('subscribers');
      return subscribers.sort((a, b) =>
        new Date(b.subscribedAt) - new Date(a.subscribedAt)
      );
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      throw error;
    }
  },

  /**
   * Get active subscribers only (admin only)
   */
  async getActiveSubscribers() {
    try {
      const subscribers = await firestoreService.query('subscribers', [
        { field: 'status', operator: '==', value: 'active' }
      ]);
      return subscribers.sort((a, b) =>
        new Date(b.subscribedAt) - new Date(a.subscribedAt)
      );
    } catch (error) {
      console.error('Error fetching active subscribers:', error);
      throw error;
    }
  },

  /**
   * Delete a subscriber (admin only)
   */
  async deleteSubscriber(subscriberId) {
    try {
      await firestoreService.delete('subscribers', subscriberId);
      return { success: true };
    } catch (error) {
      console.error('Error deleting subscriber:', error);
      throw error;
    }
  },

  /**
   * Unsubscribe via email link (public - via Cloud Function)
   */
  async unsubscribe(email) {
    try {
      await callManageSubscription({
        action: 'unsubscribe',
        email,
      });
      return { success: true };
    } catch (error) {
      console.error('Error unsubscribing:', error);
      throw error;
    }
  },

  /**
   * Get subscriber statistics (admin only)
   */
  async getStats() {
    try {
      const allSubscribers = await firestoreService.getAll('subscribers');

      const active = allSubscribers.filter(s => s.status === 'active').length;
      const unsubscribed = allSubscribers.filter(s => s.status === 'unsubscribed').length;

      // Recent signups (last 7 days)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const recent = allSubscribers.filter(s =>
        new Date(s.subscribedAt) > sevenDaysAgo && s.status === 'active'
      ).length;

      // Language breakdown
      const byLanguage = allSubscribers.reduce((acc, s) => {
        if (s.status === 'active') {
          acc[s.language] = (acc[s.language] || 0) + 1;
        }
        return acc;
      }, {});

      // Source breakdown
      const bySource = allSubscribers.reduce((acc, s) => {
        if (s.status === 'active') {
          acc[s.source] = (acc[s.source] || 0) + 1;
        }
        return acc;
      }, {});

      return {
        total: allSubscribers.length,
        active,
        unsubscribed,
        recentSignups: recent,
        byLanguage,
        bySource
      };
    } catch (error) {
      console.error('Error getting stats:', error);
      throw error;
    }
  },

  /**
   * Export subscribers to CSV format
   */
  exportToCSV(subscribers) {
    const headers = ['Email', 'Status', 'Language', 'Source', 'Subscribed At', 'Unsubscribed At'];
    const rows = subscribers.map(sub => [
      sub.email,
      sub.status,
      sub.language,
      sub.source,
      new Date(sub.subscribedAt).toLocaleString(),
      sub.unsubscribedAt ? new Date(sub.unsubscribedAt).toLocaleString() : 'N/A'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    return csvContent;
  },

  /**
   * Download CSV file
   */
  downloadCSV(subscribers, filename = 'subscribers.csv') {
    const csvContent = this.exportToCSV(subscribers);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  },

  /**
   * Get email tracking for an activity (admin only)
   */
  async getEmailTrackingForActivity(activityId) {
    try {
      const trackingDocs = await firestoreService.query('emailTracking', [
        { field: 'activityId', operator: '==', value: activityId }
      ]);
      return trackingDocs.sort((a, b) =>
        new Date(b.sentAt) - new Date(a.sentAt)
      );
    } catch (error) {
      console.error('Error fetching email tracking:', error);
      return [];
    }
  },

  /**
   * Get all email tracking records (admin only)
   */
  async getAllEmailTracking() {
    try {
      const trackingDocs = await firestoreService.getAll('emailTracking');
      return trackingDocs.sort((a, b) =>
        new Date(b.sentAt) - new Date(a.sentAt)
      );
    } catch (error) {
      console.error('Error fetching email tracking:', error);
      return [];
    }
  }
};
